import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { RootState } from "@/store";
import { HttpStatusCode } from "@/enums";
import { setCredentials, logout } from "@/store/auth/slice";
import { API_BASE_URL, API_TIMEOUT } from "@/appSettings";
import { handleTokenAndSetPermissions } from "@/utils";

const baseQuery = fetchBaseQuery({
    baseUrl: API_BASE_URL,
    prepareHeaders: (headers, { getState }) => {
        const token = (getState() as RootState).auth.accessToken;
        if (token) headers.set("authorization", `Bearer ${token}`);
        return headers;
    },
    timeout: API_TIMEOUT,
});

// Single shared in-flight refresh. While a refresh is running, every concurrent
// 401 awaits this SAME promise instead of firing its own refresh request. Without
// this, a page that fires 20-36 requests at once would, on token expiry, send the
// same refresh token N times; the backend rotates+blacklists it after the first
// success, so the rest get 401 and each dispatches logout() -> user kicked out.
let refreshPromise: Promise<string | null> | null = null;

const refreshAccessToken = async (api: Parameters<typeof baseQuery>[1]): Promise<string | null> => {
    if (!refreshPromise) {
        refreshPromise = (async (): Promise<string | null> => {
            const refreshResult = await baseQuery(
                {
                    url: "/users/token/refresh/",
                    method: "POST",
                    body: { refresh: (api.getState() as RootState).auth.refreshToken },
                },
                api,
                {},
            );

            if (refreshResult.data) {
                const data: IRefreshResponse = refreshResult.data as IRefreshResponse;

                // store the new token in the store or wherever you keep it
                api.dispatch(setCredentials(data));

                //Decode JWT Token and Assign to User's Permission
                handleTokenAndSetPermissions(data.access, api.dispatch);

                return data.access;
            }

            // refresh genuinely failed - clear the session once
            api.dispatch(logout());
            return null;
        })().finally(() => {
            // Allow a future expiry to start a fresh refresh.
            refreshPromise = null;
        });
    }

    return refreshPromise;
};

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
    args,
    api,
    extraOptions,
) => {
    let result = await baseQuery(args, api, extraOptions);

    const token = (api.getState() as RootState).auth.accessToken;

    //Decode JWT Token and Assign to User' Permission
    handleTokenAndSetPermissions(token, api.dispatch);

    if (result.error && result.error.status === HttpStatusCode.Unauthorized) {
        // All concurrent 401s queue onto the SAME refresh call.
        const newAccessToken = await refreshAccessToken(api);

        if (newAccessToken) {
            // retry the initial query (prepareHeaders re-reads the new token from state)
            result = await baseQuery(args, api, extraOptions);
        }
        // If the refresh failed, refreshAccessToken already dispatched logout();
        // return the original 401 result instead of each caller logging out again.
    }
    return result;
};

export const authApi = createApi({
    baseQuery: baseQueryWithReauth,
    tagTypes: [
        "Facility",
        "Medicine",
        "Feedbacks",
        "CommunityCenter",
        "PatientAnatomy",
        "DiagnosisNote",
        "RiskScoring",
        "RiskScoringIndices",
        "OngoingSession",
        "AlertScore",
    ],
    endpoints: () => ({}),
});
